/**
 * blockTypes: editor-side metadata for the 16 content block types.
 *
 * Derives BLOCK_TYPE_LIST from the canonical blockDefinitions module (which
 * holds the full per-block definition including display component). This module
 * strips the display-component reference and exposes only the descriptor fields
 * consumed by the picker UI and editor helpers.
 *
 * `category` groups types for the picker UI:
 *   - 'authored' : author supplies all the content (10 types).
 *   - 'dynamic'  : the block renders live platform data; little/no config
 *                  (6 types: ecosystem-orgs, ecosystem-stats, live-stats,
 *                   graph-preview, capability-cards, recent-orgs).
 */
import BLOCK_DEFINITIONS from '../blockDefinitions';

/**
 * Ordered list of block-type descriptors. Order drives the add-block picker.
 *
 * @type {Array<{type: string, label: string, description: string,
 *   icon: string, category: 'authored'|'dynamic', isWide: boolean,
 *   createDefaultData: function(): object}>}
 */
export const BLOCK_TYPE_LIST = BLOCK_DEFINITIONS.map(
    ({
        type,
        label,
        description,
        icon,
        category,
        isWide,
        createDefaultData,
    }) => ({
        type,
        label,
        description,
        icon,
        category,
        isWide,
        createDefaultData,
    })
);

/**
 * Map of `type -> descriptor` for O(1) lookup.
 *
 * @type {Object<string, object>}
 */
export const BLOCK_TYPE_MAP = BLOCK_TYPE_LIST.reduce((acc, descriptor) => {
    acc[descriptor.type] = descriptor;
    return acc;
}, {});

/**
 * Bootstrap button variants offered for CTA inputs (cta-button and
 * ecosystem-join blocks). Must stay in sync with ALLOWED_VARIANTS in
 * CtaButtonBlock.jsx and EcosystemJoinBlock.jsx. A shared import in those
 * files is not possible (blockTypes → blockDefinitions → block components
 * would be circular).
 *
 * @type {string[]}
 */
export const CTA_BUTTON_VARIANTS = [
    'primary',
    'secondary',
    'success',
    'danger',
    'warning',
    'info',
    'light',
    'dark',
    'accent',
    'link',
    'outline-primary',
    'outline-secondary',
    'outline-success',
    'outline-danger',
    'outline-warning',
    'outline-info',
    'outline-light',
    'outline-dark',
];

/**
 * Resolve the editor descriptor for a block type.
 *
 * @param {string} type - A block `type` value.
 * @returns {?object} The descriptor, or null if `type` is unknown.
 */
export const getBlockTypeDescriptor = (type) => BLOCK_TYPE_MAP[type] || null;

/**
 * Human-readable label for a block type, with a safe fallback.
 *
 * @param {string} type - A block `type` value.
 * @returns {string} The label, or the raw `type` if unknown.
 */
export const getBlockTypeLabel = (type) =>
    BLOCK_TYPE_MAP[type]?.label || type || 'Unknown block';

/**
 * Counter for synthesizing stable per-block client IDs (`_clientId`). The
 * editor uses `_clientId` as React's key so reorder / delete operations do
 * NOT reuse a BlockEditorCard / TipTap component instance for a different
 * block (round-2 review finding 6: stale TipTap content getting written
 * back to the wrong block's `data.html`).
 *
 * The counter is module-scoped, not persisted to the server, and not part
 * of the block contract: it exists purely as a React identity helper for
 * the editor session. `crypto.randomUUID` would also work; an
 * incrementing integer is smaller and avoids a runtime dependency on the
 * Web Crypto API.
 */
let nextClientIdCounter = 1;

/**
 * Generate a fresh client-side React key for a block. Exported so the load
 * path (which receives saved blocks without `_clientId`) can mint IDs for
 * existing blocks too.
 *
 * @returns {string}
 */
export const newBlockClientId = () => `cb-${nextClientIdCounter++}`;

/**
 * Ensure every block in the supplied array has a `_clientId`. Blocks already
 * carrying one keep theirs (so a save -> reload round-trip does not
 * remount editor instances unnecessarily during the same session); blocks
 * without one get a freshly minted ID. Used by the editor load path:
 * blocks loaded from the server never carry a `_clientId` because it is
 * intentionally a client-only field.
 *
 * @param {Array} blocks
 * @returns {Array} A new array with `_clientId` populated on every entry.
 */
export const ensureBlockClientIds = (blocks) =>
    (blocks || []).map((block) =>
        block && block._clientId
            ? block
            : { ...block, _clientId: newBlockClientId() }
    );

/**
 * Build a fresh block of the given type with sensible empty/default `data`
 * and the supplied `order`. Adds a `_clientId` so the editor can key the
 * block by a stable identity across reorders / deletes (review finding 6).
 *
 * @param {string} type - A block `type` value (must be a known type).
 * @param {number} order - The `order` to assign the new block.
 * @returns {?{type: string, data: object, order: number, _clientId: string}}
 *   The new block, or null if `type` is unknown.
 */
export const createBlock = (type, order) => {
    const descriptor = BLOCK_TYPE_MAP[type];
    if (!descriptor) {
        return null;
    }
    return {
        type,
        data: descriptor.createDefaultData(),
        order,
        _clientId: newBlockClientId(),
    };
};
